import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// /home/ubuntu/avaai_dashboard/frontend/src/pages/games/ChessGame.tsx
import { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chess } from "chess.js";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, ContactShadows, RoundedBox, Text } from "@react-three/drei";
const FILES = ["a", "b", "c", "d", "e", "f", "g", "h"];
const RANKS = [8, 7, 6, 5, 4, 3, 2, 1];
const PIECE_ICONS = {
    wp: "♙",
    wr: "♖",
    wn: "♘",
    wb: "♗",
    wq: "♕",
    wk: "♔",
    bp: "♟",
    br: "♜",
    bn: "♞",
    bb: "♝",
    bq: "♛",
    bk: "♚",
};
function squareName(fileIndex, rank) {
    return `${FILES[fileIndex]}${rank}`;
}
function statusText(game) {
    if (game.isCheckmate()) {
        return game.turn() === "w" ? "Checkmate — Black wins" : "Checkmate — White wins";
    }
    if (game.isStalemate())
        return "Stalemate";
    if (game.isDraw())
        return "Draw";
    if (game.isCheck()) {
        return game.turn() === "w" ? "White is in check" : "Black is in check";
    }
    return game.turn() === "w" ? "White to move" : "Black to move";
}
function pieceLabel(piece) {
    switch (piece) {
        case "p":
            return "Pawn";
        case "r":
            return "Rook";
        case "n":
            return "Knight";
        case "b":
            return "Bishop";
        case "q":
            return "Queen";
        case "k":
            return "King";
        default:
            return "Piece";
    }
}
export default function ChessGame() {
    const navigate = useNavigate();
    const [game, setGame] = useState(() => new Chess());
    const [selected, setSelected] = useState(null);
    const [legalTargets, setLegalTargets] = useState([]);
    const [moveHistory, setMoveHistory] = useState([]);
    const [orientation, setOrientation] = useState("white");
    const [boardTilt, setBoardTilt] = useState({ x: 18, y: -12 });
    const dragRef = useRef({
        active: false,
        startX: 0,
        startY: 0,
        startTiltX: 10,
        startTiltY: 0,
    });
    const [avaTip, setAvaTip] = useState("Welcome to AVAAIO Chess. Select one of your pieces to see legal move targets.");
    const [avaDifficulty, setAvaDifficulty] = useState("easy");
    const board = useMemo(() => game.board(), [game]);
    function refreshFromGame(nextGame, tip) {
        setGame(new Chess(nextGame.fen()));
        const verboseMoves = nextGame.history({ verbose: true }).map((m) => ({
            san: m.san,
            from: m.from,
            to: m.to,
            color: m.color,
            piece: m.piece,
        }));
        setMoveHistory(verboseMoves);
        setAvaTip(tip || "Position updated.");
    }
    function clearSelection(message) {
        setSelected(null);
        setLegalTargets([]);
        if (message)
            setAvaTip(message);
    }
    function avaChessBanter(kind) {
        const normalLines = [
            "I saw that coming 😄",
            "Careful... Ava is reading the board now.",
            "Nice try. My circuits liked that move.",
            "I respect the move. I still have plans.",
            "That was cute. My turn to cook.",
            "You brought courage. I brought calculation.",
            "Friendly warning: I am getting sharper.",
            "That square looked safe... until Ava arrived.",
            "Interesting plan. Let's test it.",
            "That move has consequences.",
            "I'm starting to like this position.",
            "You're making me think now.",
        ];
        const checkLines = [
            "Check. No panic... okay maybe a little panic 😄",
            "Check. Ava has entered boss mode.",
            "Your king is feeling the pressure now.",
            "Check. I promise I am smiling politely.",
            "That square looked safer a moment ago.",
        ];
        const mateLines = [
            "Checkmate. Good game — run it back?",
            "Ava wins this one. Rematch? I know you want it.",
            "That was a battle. Ava claims the board.",
        ];
        const drawLines = [
            "Draw game. You survived Ava this time.",
            "A draw? Respect. Rematch and settle it?",
            "Even match. Ava approves.",
        ];
        const pool = kind === "mate" ? mateLines :
            kind === "draw" ? drawLines :
                kind === "check" ? checkLines :
                    normalLines;
        return pool[Math.floor(Math.random() * pool.length)];
    }
    function chooseAvaChessMove(nextGame) {
        const moves = nextGame.moves({ verbose: true });
        if (!moves.length)
            return null;
        const pieceValues = {
            p: 100,
            n: 320,
            b: 330,
            r: 500,
            q: 900,
            k: 0,
        };
        const centerSquares = new Set(["d4", "e4", "d5", "e5"]);
        const extendedCenter = new Set(["c3", "d3", "e3", "f3", "c4", "f4", "c5", "f5", "c6", "d6", "e6", "f6"]);
        function materialScore(position) {
            let total = 0;
            const boardState = position.board();
            for (const row of boardState) {
                for (const piece of row) {
                    if (!piece)
                        continue;
                    const value = pieceValues[piece.type] || 0;
                    total += piece.color === "b" ? value : -value;
                }
            }
            return total;
        }
        function staticScore(position) {
            let score = materialScore(position);
            if (position.isCheckmate()) {
                score += position.turn() === "w" ? 100000 : -100000;
            }
            if (position.isCheck()) {
                score += position.turn() === "w" ? 180 : -180;
            }
            score += position.moves({ verbose: true }).length * (position.turn() === "b" ? 2 : -2);
            return score;
        }
        function scoreMove(baseGame, move, depth) {
            const test = new Chess(baseGame.fen());
            test.move({ from: move.from, to: move.to, promotion: move.promotion || "q" });
            let score = staticScore(test);
            if (move.captured)
                score += (pieceValues[move.captured] || 100) * 1.25;
            if (move.promotion)
                score += 780;
            if (test.isCheckmate())
                score += 100000;
            if (test.isCheck())
                score += avaDifficulty === "veryHard" ? 520 : avaDifficulty === "hard" ? 380 : 260;
            if (centerSquares.has(move.to))
                score += avaDifficulty === "veryHard" ? 70 : 45;
            if (extendedCenter.has(move.to))
                score += avaDifficulty === "veryHard" ? 34 : 20;
            if (["n", "b"].includes(move.piece))
                score += avaDifficulty === "easy" ? 24 : 38;
            if (move.san === "O-O" || move.san === "O-O-O")
                score += 90;
            const replies = test.moves({ verbose: true });
            if (replies.length) {
                let playerBestReply = -999999;
                for (const reply of replies) {
                    const replyBoard = new Chess(test.fen());
                    replyBoard.move({ from: reply.from, to: reply.to, promotion: reply.promotion || "q" });
                    let replyScore = 0;
                    if (reply.captured)
                        replyScore += pieceValues[reply.captured] || 100;
                    if (reply.promotion)
                        replyScore += 780;
                    if (replyBoard.isCheckmate())
                        replyScore += 100000;
                    if (replyBoard.isCheck())
                        replyScore += 240;
                    replyScore -= materialScore(replyBoard);
                    playerBestReply = Math.max(playerBestReply, replyScore);
                }
                const replyPenalty = avaDifficulty === "veryHard" ? 1.05 :
                    avaDifficulty === "hard" ? 0.82 :
                        avaDifficulty === "medium" ? 0.58 :
                            0.42;
                score -= playerBestReply * replyPenalty;
            }
            if (depth > 1 && (avaDifficulty === "hard" || avaDifficulty === "veryHard")) {
                const futureMoves = test.moves({ verbose: true });
                let bestFuture = -999999;
                const futureLimit = avaDifficulty === "veryHard" ? 28 : 10;
                const futureWeight = avaDifficulty === "veryHard" ? 0.42 : 0.28;
                for (const future of futureMoves.slice(0, futureLimit)) {
                    bestFuture = Math.max(bestFuture, scoreMove(test, future, depth - 1) * futureWeight);
                }
                if (bestFuture > -999999)
                    score += bestFuture;
            }
            const noise = avaDifficulty === "veryHard" ? 1.5 :
                avaDifficulty === "hard" ? 14 :
                    avaDifficulty === "medium" ? 36 :
                        62;
            score += Math.random() * noise;
            return score;
        }
        const depth = avaDifficulty === "veryHard" ? 3 :
            avaDifficulty === "hard" ? 2 :
                1;
        const scored = moves.map((m) => ({
            move: m,
            score: scoreMove(nextGame, m, depth),
        }));
        scored.sort((a, b) => b.score - a.score);
        const pickPool = avaDifficulty === "veryHard" ? scored.slice(0, 1) :
            avaDifficulty === "hard" ? scored.slice(0, Math.min(2, scored.length)) :
                avaDifficulty === "medium" ? scored.slice(0, Math.min(3, scored.length)) :
                    scored.slice(0, Math.min(4, scored.length));
        return pickPool[Math.floor(Math.random() * pickPool.length)].move;
    }
    function letAvaMove(afterUserMove) {
        if (afterUserMove.isGameOver()) {
            refreshFromGame(afterUserMove, afterUserMove.isCheckmate()
                ? "Congratulations! You checkmated Ava. Want a rematch?"
                : "Game over. Want a rematch?");
            return;
        }
        if (afterUserMove.turn() !== "b") {
            refreshFromGame(afterUserMove, "Your move is complete.");
            return;
        }
        const avaMove = chooseAvaChessMove(afterUserMove);
        if (!avaMove) {
            refreshFromGame(afterUserMove, "Ava has no legal move. Want a rematch?");
            return;
        }
        const next = new Chess(afterUserMove.fen());
        const moved = next.move({
            from: avaMove.from,
            to: avaMove.to,
            promotion: avaMove.promotion || "q",
        });
        const finalTip = next.isCheckmate()
            ? "Ava wins by checkmate. Good game — want a rematch?"
            : next.isDraw()
                ? "Draw game. Want a rematch?"
                : next.isCheck()
                    ? `Ava moved ${moved?.san}. You are in check.`
                    : `Ava moved ${moved?.san}. Your turn. • Ava Thinking Ready.`;
        refreshFromGame(next, finalTip);
    }
    function handleSquareClick(sq) {
        const piece = game.get(sq);
        if (selected && legalTargets.includes(sq)) {
            const next = new Chess(game.fen());
            const moveResult = next.move({
                from: selected,
                to: sq,
                promotion: "q",
            });
            if (moveResult) {
                clearSelection();
                letAvaMove(next);
                return;
            }
        }
        if (!piece) {
            clearSelection("That square is empty.");
            return;
        }
        const turnColor = game.turn();
        if (turnColor === "b") {
            clearSelection("Ava is thinking. Wait for Ava's move.");
            return;
        }
        if (piece.color !== turnColor) {
            clearSelection(turnColor === "w"
                ? "It is White’s turn."
                : "It is Black’s turn.");
            return;
        }
        const moves = game.moves({ square: sq, verbose: true });
        if (!moves.length) {
            clearSelection("That piece has no legal moves right now.");
            return;
        }
        setSelected(sq);
        setLegalTargets(moves.map((m) => m.to));
        setAvaTip(`${piece.color === "w" ? "White" : "Black"} ${pieceLabel(piece.type)} selected on ${sq}. ${moves.length} legal move${moves.length === 1 ? "" : "s"} available.`);
    }
    function resetGame() {
        const next = new Chess();
        refreshFromGame(next, "Board reset. White moves first.");
        clearSelection();
    }
    function undoMove() {
        const next = new Chess(game.fen());
        const undone = next.undo();
        if (!undone) {
            setAvaTip("There is no move to undo.");
            return;
        }
        refreshFromGame(next, `Undid ${undone.san}. Review the position and try again.`);
        clearSelection();
    }
    function toggleOrientation() {
        setOrientation((prev) => (prev === "white" ? "black" : "white"));
    }
    const renderedRanks = orientation === "white" ? [...RANKS] : [...RANKS].reverse();
    const renderedFiles = orientation === "white" ? [...FILES] : [...FILES].reverse();
    const lastMove = moveHistory.length ? moveHistory[moveHistory.length - 1] : null;
    const gameOver = game.isGameOver();
    const endTitle = game.isCheckmate()
        ? game.turn() === "b"
            ? "Congratulations!"
            : "Ava Wins"
        : game.isStalemate()
            ? "Stalemate"
            : game.isDraw()
                ? "Draw Game"
                : "";
    const endMessage = game.isCheckmate()
        ? game.turn() === "b"
            ? "You checkmated Ava. Excellent match."
            : "Ava won by checkmate. Ready for a rematch?"
        : game.isStalemate()
            ? "Stalemate — no legal moves remain. Tap Play Rematch to start a new game."
            : game.isDraw()
                ? "The game ended in a draw. Tap Play Rematch to start a new game."
                : "";
    const shellStyle = {
        minHeight: "100vh",
        padding: "18px 14px 24px",
        background: "radial-gradient(circle at top, rgba(0,255,255,0.14) 0%, rgba(0,255,255,0.04) 22%, transparent 42%), linear-gradient(180deg, #05070d 0%, #06111d 52%, #010203 100%)",
        perspective: "1800px",
        color: "#ecfaff",
        boxSizing: "border-box",
    };
    const pageStyle = {
        maxWidth: 1480,
        margin: "0 auto",
        display: "grid",
        gap: 14,
    };
    const topPanel = {
        borderRadius: 22,
        padding: "18px 18px 16px",
        background: "rgba(7, 13, 23, 0.82)",
        border: "1px solid rgba(0,255,255,0.16)",
        boxShadow: "0 0 22px rgba(0,255,255,0.06), inset 0 0 18px rgba(255,255,255,0.02)",
        backdropFilter: "blur(10px)",
    };
    const gridStyle = {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.45fr) minmax(320px, 0.72fr)",
        gap: 14,
        alignItems: "start",
    };
    const boardPanel = {
        borderRadius: 22,
        padding: 16,
        background: "rgba(7, 13, 23, 0.82)",
        border: "1px solid rgba(0,255,255,0.16)",
        boxShadow: "0 0 22px rgba(0,255,255,0.06), inset 0 0 18px rgba(255,255,255,0.02)",
        backdropFilter: "blur(10px)",
    };
    const sidePanel = {
        display: "grid",
        gap: 24,
    };
    const cardStyle = {
        borderRadius: 18,
        padding: 14,
        background: "linear-gradient(180deg, rgba(10,16,28,0.96) 0%, rgba(5,10,18,0.98) 100%)",
        border: "1px solid rgba(90,220,255,0.18)",
        boxShadow: "0 8px 22px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.03)",
    };
    const buttonStyle = {
        minHeight: 44,
        borderRadius: 12,
        border: "1px solid rgba(90,220,255,0.24)",
        background: "linear-gradient(180deg, rgba(10,18,31,0.96) 0%, rgba(6,12,22,0.98) 100%)",
        color: "#ecfdff",
        fontSize: 14,
        fontWeight: 800,
        cursor: "pointer",
        padding: "0 14px",
        boxShadow: "0 0 12px rgba(0,255,255,0.12)",
    };
    function startBoardDrag(clientX, clientY) {
        dragRef.current = {
            active: true,
            startX: clientX,
            startY: clientY,
            startTiltX: boardTilt.x,
            startTiltY: boardTilt.y,
        };
    }
    function moveBoardDrag(clientX, clientY) {
        if (!dragRef.current.active)
            return;
        const dx = clientX - dragRef.current.startX;
        const dy = clientY - dragRef.current.startY;
        const nextX = Math.max(-4, Math.min(28, dragRef.current.startTiltX - dy * 0.12));
        const nextY = Math.max(-38, Math.min(38, dragRef.current.startTiltY + dx * 0.14));
        setBoardTilt({ x: nextX, y: nextY });
    }
    function stopBoardDrag() {
        dragRef.current.active = false;
    }
    return (_jsx("div", { className: "ava-chess-shell", style: shellStyle, children: _jsxs("div", { style: pageStyle, children: [_jsxs("div", { style: topPanel, children: [_jsx("div", { style: {
                                fontSize: 11,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                                color: "#79f3ff",
                                fontWeight: 800,
                                marginBottom: 6,
                            }, children: "AVAAIO Game System" }), _jsx("h1", { style: {
                                margin: 0,
                                fontSize: "clamp(32px, 4vw, 52px)",
                                lineHeight: 1,
                                fontWeight: 900,
                                color: "#19e6ff",
                                textShadow: "0 0 16px rgba(0,255,255,0.20)",
                            }, children: "AVAAIO Chess" }), _jsx("p", { style: {
                                margin: "10px 0 0",
                                fontSize: "clamp(13px, 1.5vw, 17px)",
                                color: "rgba(236,250,255,0.84)",
                                maxWidth: 920,
                                lineHeight: 1.45,
                            }, children: "Full board interaction, move tracking, check/checkmate detection, and an Ava guidance panel." })] }), _jsxs("div", { className: "ava-chess-stack", style: gridStyle, children: [_jsxs("div", { style: boardPanel, children: [_jsxs("div", { className: "ava-chess-topbar", style: {
                                        display: "flex",
                                        justifyContent: "space-between",
                                        gap: 10,
                                        alignItems: "center",
                                        flexWrap: "wrap",
                                        marginBottom: 14,
                                    }, children: [_jsxs("div", { children: [_jsx("div", { style: { fontSize: 12, textTransform: "uppercase", letterSpacing: "0.16em", color: "#79f3ff", fontWeight: 800 }, children: "Live Match" }), _jsx("div", { style: { fontSize: 16, fontWeight: 900 }, children: statusText(game) })] }), _jsxs("div", { className: "ava-chess-controls", style: { display: "flex", gap: 8, flexWrap: "wrap" }, children: [_jsx("button", { type: "button", onClick: undoMove, style: buttonStyle, children: "Undo" }), _jsx("button", { type: "button", onClick: resetGame, style: buttonStyle, children: "Reset" }), _jsx("button", { type: "button", onClick: toggleOrientation, style: buttonStyle, children: "Flip Board" }), _jsx("button", { type: "button", onClick: () => setBoardTilt({ x: 18, y: -12 }), style: buttonStyle, children: "Reset View" }), ["easy", "medium", "hard"].map((level) => (_jsx("button", { type: "button", onClick: () => setAvaDifficulty(level), style: {
                                                        ...buttonStyle,
                                                        background: avaDifficulty === level
                                                            ? level === "easy"
                                                                ? "linear-gradient(135deg,#43d98d,#127a58)"
                                                                : level === "medium"
                                                                    ? "linear-gradient(135deg,#5cf4ff,#147dff)"
                                                                    : "linear-gradient(135deg,#b06cff,#5a2cff)"
                                                            : buttonStyle.background,
                                                        textTransform: "capitalize",
                                                    }, children: level }, level)))] })] }), _jsxs("div", { className: "ava-chess-board-canvas", style: {
                                        position: "relative",
                                        height: "min(78vh, 92vw)",
                                        minHeight: 320,
                                        maxHeight: "92vw",
                                        aspectRatio: "1 / 1",
                                        width: "100%",
                                        borderRadius: 28,
                                        overflow: "hidden",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                        background: "radial-gradient(circle at top, #172d48 0%, #05070d 52%, #000 100%)",
                                        boxShadow: "0 24px 70px rgba(0,0,0,0.6)",
                                    }, children: [_jsxs(Canvas, { shadows: true, camera: { position: [9.5, 7.4, 9.1], fov: 36 }, children: [_jsx("ambientLight", { intensity: 0.5 }), _jsx("directionalLight", { position: [4, 10, 5], intensity: 3.4, castShadow: true }), _jsx("pointLight", { position: [-4, 3, -4], intensity: 2.2, color: "#ffffff" }), _jsx("hemisphereLight", { intensity: 1.0, color: "#d8efff", groundColor: "#111827" }), _jsx("directionalLight", { position: [-5, 6, -3], intensity: 1.35, color: "#9fdcff" }), _jsx("group", { rotation: [0, -0.48, 0], position: [0, -0.35, 0], children: renderedRanks.map((rank, rankIndex) => renderedFiles.map((file, fileIndex) => {
                                                        const actualFileIndex = FILES.indexOf(file);
                                                        const sq = squareName(actualFileIndex, rank);
                                                        const piece = game.get(sq);
                                                        const isDark = (actualFileIndex + rank) % 2 === 1;
                                                        const isSelected = selected === sq;
                                                        const isTarget = legalTargets.includes(sq);
                                                        return (_jsxs("group", { position: [
                                                                fileIndex - 3.5,
                                                                0,
                                                                rankIndex - 3.5
                                                            ], onClick: () => handleSquareClick(sq), children: [_jsx(RoundedBox, { args: [1, 0.18, 1], radius: 0.05, smoothness: 4, children: _jsx("meshPhysicalMaterial", { color: isDark ? "#050505" : "#f8f8f8", metalness: 0.15, roughness: 0.28, clearcoat: 1, emissive: isDark ? "#000000" : "#ffffff", emissiveIntensity: 0 }) }), false && (_jsx(Text, { position: [
                                                                        -0.33,
                                                                        0.255,
                                                                        0.33,
                                                                    ], rotation: [-Math.PI / 2, 0, 0], fontSize: 0.11, anchorX: "center", anchorY: "middle", renderOrder: 999, depthOffset: -10, color: isDark ? "#ffffff" : "#111111", outlineColor: isDark ? "#00f5ff" : "#ffd35c", outlineWidth: 0.03, children: `${file.toUpperCase()}${rank}` })), piece && (_jsxs(_Fragment, { children: [_jsxs("mesh", { position: [0, 0.18, 0], castShadow: true, children: [_jsx("cylinderGeometry", { args: [0.40, 0.52, 0.20, 96] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0, 0.34, 0], castShadow: true, children: [_jsx("torusGeometry", { args: [0.34, 0.052, 20, 96] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.03, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0, 0.62, 0], castShadow: true, children: [_jsx("cylinderGeometry", { args: [0.24, 0.36, piece.type === "k" || piece.type === "q" ? 0.84 : 0.64, 96] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), piece.type === "p" && (_jsxs("mesh", { position: [0, 1.06, 0], castShadow: true, children: [_jsx("sphereGeometry", { args: [0.28, 64, 64] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] })), piece.type === "r" && (_jsxs("group", { position: [0, 1.06, 0], children: [_jsxs("mesh", { castShadow: true, children: [_jsx("cylinderGeometry", { args: [0.37, 0.40, 0.26, 8] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), [0, 1, 2, 3].map((i) => (_jsxs("mesh", { position: [Math.cos(i * Math.PI / 2) * 0.27, 0.19, Math.sin(i * Math.PI / 2) * 0.27], castShadow: true, children: [_jsx("boxGeometry", { args: [0.19, 0.25, 0.19] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }, i)))] })), piece.type === "n" && (_jsxs("group", { position: [0, 0.94, 0], children: [_jsxs("mesh", { position: [0, 0.02, 0], rotation: [0.05, 0, -0.18], castShadow: true, children: [_jsx("cylinderGeometry", { args: [0.22, 0.31, 0.64, 48] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0.05, 0.34, -0.1], rotation: [0.25, 0, -0.08], castShadow: true, children: [_jsx("boxGeometry", { args: [0.36, 0.40, 0.52] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0.04, 0.4, -0.34], rotation: [1.18, 0, 0], castShadow: true, children: [_jsx("coneGeometry", { args: [0.18, 0.48, 40] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0.16, 0.55, 0.04], rotation: [0.25, 0, 0.45], castShadow: true, children: [_jsx("coneGeometry", { args: [0.085, 0.32, 24] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [-0.1, 0.54, 0.03], rotation: [0.25, 0, -0.45], castShadow: true, children: [_jsx("coneGeometry", { args: [0.085, 0.32, 24] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0.11, 0.37, -0.32], castShadow: true, children: [_jsx("sphereGeometry", { args: [0.052, 20, 20] }), _jsx("meshBasicMaterial", { color: piece.color === "w" ? "#050505" : "#ffffff" })] }), _jsxs("mesh", { position: [0, 0.24, 0.18], castShadow: true, children: [_jsx("boxGeometry", { args: [0.10, 0.40, 0.06] }), _jsx("meshBasicMaterial", { color: piece.color === "w" ? "#d4af37" : "#222222" })] })] })), piece.type === "b" && (_jsxs("group", { position: [0, 1.04, 0], children: [_jsxs("mesh", { castShadow: true, children: [_jsx("sphereGeometry", { args: [0.285, 64, 64] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0, 0.34, 0], castShadow: true, children: [_jsx("coneGeometry", { args: [0.22, 0.54, 64] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0.04, 0.05, -0.23], rotation: [0.55, 0, 0.1], castShadow: true, children: [_jsx("boxGeometry", { args: [0.06, 0.42, 0.06] }), _jsx("meshBasicMaterial", { color: piece.color === "w" ? "#d4af37" : "#ffffff" })] })] })), piece.type === "q" && (_jsxs("group", { position: [0, 1.12, 0], children: [_jsxs("mesh", { castShadow: true, children: [_jsx("sphereGeometry", { args: [0.285, 64, 64] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), [0, 1, 2, 3, 4, 5, 6].map((i) => (_jsxs("mesh", { position: [Math.cos(i * Math.PI / 3.5) * 0.28, 0.28, Math.sin(i * Math.PI / 3.5) * 0.28], castShadow: true, children: [_jsx("sphereGeometry", { args: [0.092, 24, 24] }), _jsx("meshPhysicalMaterial", { color: "#d4af37", metalness: 1, roughness: 0.035, clearcoat: 1, emissive: "#ffd700", emissiveIntensity: 0.08 })] }, i)))] })), piece.type === "k" && (_jsxs("group", { position: [0, 1.12, 0], children: [_jsxs("mesh", { castShadow: true, children: [_jsx("sphereGeometry", { args: [0.285, 64, 64] }), _jsx("meshPhysicalMaterial", { color: piece.color === "w" ? "#ffffff" : "#05070d", metalness: 0.15, roughness: 0.035, clearcoat: 1, clearcoatRoughness: 0.02, reflectivity: 1, envMapIntensity: 0.15 })] }), _jsxs("mesh", { position: [0, 0.42, 0], castShadow: true, children: [_jsx("boxGeometry", { args: [0.115, 0.74, 0.115] }), _jsx("meshPhysicalMaterial", { color: "#d4af37", metalness: 1, roughness: 0.035, clearcoat: 1, emissive: "#ffd700", emissiveIntensity: 0.08 })] }), _jsxs("mesh", { position: [0, 0.56, 0], castShadow: true, children: [_jsx("boxGeometry", { args: [0.50, 0.115, 0.115] }), _jsx("meshPhysicalMaterial", { color: "#d4af37", metalness: 1, roughness: 0.035, clearcoat: 1, emissive: "#ffd700", emissiveIntensity: 0.08 })] })] }))] }))] }, sq));
                                                    })) }), _jsx(ContactShadows, { position: [0, -1, 0], opacity: 0.8, scale: 16, blur: 2.6, far: 8 }), _jsx(OrbitControls, { enablePan: false, minDistance: 5, maxDistance: 15 })] }), gameOver && (_jsx("div", { className: "ava-chess-end-overlay", role: "dialog", "aria-live": "polite", children: _jsxs("div", { className: "ava-chess-end-card", children: [_jsx("div", { className: "ava-chess-end-kicker", children: "Match Complete" }), _jsx("div", { className: "ava-chess-end-title", children: endTitle }), _jsx("div", { className: "ava-chess-end-message", children: endMessage }), _jsx("button", { type: "button", onClick: resetGame, className: "ava-chess-rematch-button", children: "Play Rematch" })] }) }))] })] }), _jsxs("div", { style: sidePanel, children: [_jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: 16, fontWeight: 950, marginBottom: 12, color: "#79f3ff" }, children: "Ava Chess Chat" }), _jsxs("div", { className: "ava-chess-chatbox", children: [_jsx("div", { className: "ava-chess-avatar", children: "A" }), _jsxs("div", { className: "ava-chess-bubble", children: [_jsx("div", { className: "ava-chess-name", children: "Ava" }), _jsx("div", { children: avaTip }), gameOver && (_jsx("button", { type: "button", onClick: resetGame, className: "ava-chess-chat-rematch", children: "Play Rematch" }))] })] })] }), _jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: 16, fontWeight: 900, marginBottom: 8 }, children: "Position Status" }), _jsxs("div", { style: { display: "grid", gap: 6, fontSize: 14, color: "rgba(236,250,255,0.84)" }, children: [_jsxs("div", { children: [_jsx("strong", { children: "Turn:" }), " ", game.turn() === "w" ? "White" : "Black"] }), _jsxs("div", { children: [_jsx("strong", { children: "Check:" }), " ", game.isCheck() ? "Yes" : "No"] }), _jsxs("div", { children: [_jsx("strong", { children: "Checkmate:" }), " ", game.isCheckmate() ? "Yes" : "No"] }), _jsxs("div", { children: [_jsx("strong", { children: "Stalemate:" }), " ", game.isStalemate() ? "Yes" : "No"] }), _jsxs("div", { children: [_jsx("strong", { children: "Draw:" }), " ", game.isDraw() ? "Yes" : "No"] }), _jsxs("div", { children: [_jsx("strong", { children: "Move Count:" }), " ", moveHistory.length] })] })] }), _jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: 16, fontWeight: 900, marginBottom: 8 }, children: "Move History" }), _jsx("div", { style: {
                                                maxHeight: 260,
                                                overflowY: "auto",
                                                display: "grid",
                                                gap: 8,
                                                paddingRight: 4,
                                            }, children: moveHistory.length === 0 ? (_jsx("div", { style: { color: "rgba(236,250,255,0.66)", fontSize: 14 }, children: "No moves yet. White starts." })) : (moveHistory.map((move, index) => (_jsxs("div", { style: {
                                                    padding: "8px 10px",
                                                    borderRadius: 12,
                                                    background: "rgba(255,255,255,0.03)",
                                                    border: "1px solid rgba(90,220,255,0.12)",
                                                    fontSize: 13,
                                                    lineHeight: 1.45,
                                                }, children: [_jsxs("strong", { children: ["#", index + 1] }), " ", move.color === "w" ? "White" : "Black", " \u2022 ", move.san, _jsx("br", {}), _jsxs("span", { style: { opacity: 0.82 }, children: [move.from, " \u2192 ", move.to] })] }, `${move.from}-${move.to}-${index}`)))) })] }), _jsxs("div", { style: cardStyle, children: [_jsx("div", { style: { fontSize: 16, fontWeight: 900, marginBottom: 8 }, children: "Navigation" }), _jsxs("div", { style: { display: "grid", gap: 8 }, children: [_jsx("button", { type: "button", onClick: () => navigate("/games"), style: buttonStyle, children: "Back to Games Hub" }), _jsx("button", { type: "button", onClick: () => navigate("/education"), style: buttonStyle, children: "Open Education Center" })] })] })] })] }), _jsx("style", { children: `
          .ava-chess-coord-overlay {
            position: absolute;
            inset: 0;
            z-index: 12;
            pointer-events: none;
            font-weight: 950;
            color: #ffd35c;
            text-shadow: 0 0 7px rgba(0,0,0,0.95), 0 0 10px rgba(255,211,92,0.8), 0 0 2px #000;
          }

          .ava-chess-file-labels {
            position: absolute;
            left: 7.5%;
            right: 7.5%;
            bottom: 4.2%;
            display: grid;
            grid-template-columns: repeat(8, 1fr);
            text-align: center;
            font-size: clamp(18px, 2.5vw, 30px);
          }

          .ava-chess-rank-labels {
            position: absolute;
            top: 7.5%;
            bottom: 7.5%;
            left: 3.6%;
            display: grid;
            grid-template-rows: repeat(8, 1fr);
            align-items: center;
            font-size: clamp(18px, 2.5vw, 30px);
          }

          .ava-chess-end-overlay {
            position: absolute;
            inset: 0;
            z-index: 30;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 18px;
            background: radial-gradient(circle at center, rgba(0,255,255,0.12), rgba(0,0,0,0.62) 62%);
            backdrop-filter: blur(4px);
          }

          .ava-chess-end-card {
            width: min(420px, 92%);
            border-radius: 24px;
            padding: 22px 18px;
            text-align: center;
            background: linear-gradient(180deg, rgba(8,16,28,0.96), rgba(2,6,12,0.98));
            border: 1px solid rgba(255,211,92,0.42);
            box-shadow: 0 0 36px rgba(255,193,7,0.2), inset 0 0 20px rgba(255,255,255,0.035);
          }

          .ava-chess-end-title {
            font-size: clamp(30px, 6vw, 52px);
            font-weight: 950;
            color: #ffd35c;
            margin-bottom: 10px;
          }

          .ava-chess-end-kicker,
          .ava-chess-name {
            font-size: 12px;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            color: #79f3ff;
            font-weight: 950;
            margin-bottom: 4px;
          }

          .ava-chess-end-message {
            font-size: clamp(14px, 2.4vw, 17px);
            line-height: 1.45;
            color: rgba(236,250,255,0.9);
            margin-bottom: 16px;
          }

          .ava-chess-rematch-button,
          .ava-chess-chat-rematch {
            width: 100%;
            min-height: 48px;
            border: 0;
            border-radius: 14px;
            font-size: 16px;
            font-weight: 950;
            color: #090909;
            background: linear-gradient(135deg,#ffd35c,#ff8a00);
            box-shadow: 0 0 20px rgba(255,193,7,0.45);
            cursor: pointer;
          }

          .ava-chess-chatbox {
            display: flex;
            align-items: flex-start;
            gap: 10px;
            padding: 12px;
            border-radius: 18px;
            background: rgba(0,255,255,0.055);
            border: 1px solid rgba(90,220,255,0.18);
          }

          .ava-chess-avatar {
            width: 38px;
            height: 38px;
            flex: 0 0 38px;
            border-radius: 999px;
            display: grid;
            place-items: center;
            color: #041016;
            font-weight: 950;
            background: linear-gradient(135deg,#5cf4ff,#ffd35c);
          }

          .ava-chess-bubble {
            flex: 1;
            border-radius: 16px;
            padding: 10px 12px;
            background: linear-gradient(180deg, rgba(13,24,39,0.96), rgba(5,11,20,0.98));
            border: 1px solid rgba(255,255,255,0.08);
            color: rgba(236,250,255,0.9);
            line-height: 1.5;
            font-size: 14px;
          }

          @media (max-width: 1080px) {
            .ava-chess-stack {
              grid-template-columns: 1fr;
            }
          }

          @media (max-width: 768px) {
            .ava-chess-shell {
              padding:
                max(10px, env(safe-area-inset-top))
                10px
                max(18px, env(safe-area-inset-bottom))
                10px !important;
              overflow-x: hidden;
            }

            .ava-chess-board-wrap {
              width: 100%;
              max-width: 96vw;
              margin: 0 auto;
            }
          }
        
            @media (max-width: 768px) {
              .ava-chess-stack {
                grid-template-columns: 1fr !important;
              }

              .ava-chess-topbar {
                display: grid !important;
                grid-template-columns: 1fr !important;
                gap: 12px !important;
                width: 100% !important;
              }

              .ava-chess-controls {
                width: 100% !important;
                display: grid !important;
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                gap: 10px !important;
              }

              .ava-chess-controls button {
                width: 100% !important;
                min-width: 0 !important;
                white-space: normal !important;
              }

              .ava-chess-board-wrap {
                width: 100% !important;
                max-width: 96vw !important;
                margin: 0 auto !important;
              }
            }


            @media (max-width: 768px) {
              .ava-chess-stack {
                display: flex !important;
                flex-direction: column !important;
                width: 100% !important;
                gap: 12px !important;
              }

              .ava-chess-topbar {
                display: flex !important;
                flex-direction: column !important;
                align-items: stretch !important;
                width: 100% !important;
                gap: 12px !important;
              }

              .ava-chess-controls {
                display: grid !important;
                grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                width: 100% !important;
                gap: 8px !important;
              }

              .ava-chess-controls button {
                width: 100% !important;
                min-width: 0 !important;
                padding: 10px 8px !important;
                font-size: 12px !important;
              }

              .ava-chess-board-canvas {
                width: 100% !important;
                height: 72vw !important;
                min-height: 300px !important;
                max-height: 420px !important;
                border-radius: 18px !important;
              }
            }

            @media (max-width: 430px) {
              .ava-chess-board-canvas {
                height: 78vw !important;
                min-height: 280px !important;
                max-height: 360px !important;
              }
            }

          ` })] }) }));
}
